import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import './UploadDashboard.css';
import {
  FiUpload,
  FiCheck,
  FiX,
  FiZap,
  FiHome,
  FiTruck,
  FiLayers,
  FiFileText,
  FiLoader,
  FiDatabase,
  FiFilter,
  FiRefreshCw,
  FiBriefcase,
  FiMail,
} from 'react-icons/fi';
import { Table } from 'reactstrap';
import { useSelector } from 'react-redux';
import { postUploadCatastro, postUploadCiudadLimpia, postUploadRadicadosDiarios, postUploadEscombros, postUploadCau, postUploadDatosBasicos, getUploadList, postUploadProcesosComerciales, postUploadCorrespondencia } from '../../helpers/backend';
import FileTableRow from './FileTableRow';
import useInfiniteScroll from '../../hooks/useInfiniteScroll';
import DateUploadModal from './DateUploadModal';

const UploadDashboard = () => {
  const [uploadStatus, setUploadStatus] = useState({});
  const [recentFiles, setRecentFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  // Scroll infinito
  const {
    data: filesData,
    loading: loadingMore,
    hasMore,
    count,
    resetData,
    lastElementRef,
    updateItems,
    setObserverRoot,
    setUserScrolled,
    loadMore
  } = useInfiniteScroll(getUploadList);
  const tableWrapperRef = useRef(null);

  // Estados para filtros
  const [filters, setFilters] = useState({
    tipo: '',
    usuario: '',
    fechaDesde: '',
    fechaHasta: ''
  });
  const [filteredFiles, setFilteredFiles] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  // Estado para modal de Procesos Comerciales (Fuente + archivo)
  const [commercialModal, setCommercialModal] = useState({
    isOpen: false,
    title: 'Subir Procesos Comerciales',
    description: 'Selecciona la Fuente y el archivo de procesos comerciales',
    acceptedTypes: '.txt',
  });

  // Estados para el modal de fecha
  const [modalState, setModalState] = useState({
    isOpen: false,
    uploadType: null,
    title: '',
    description: '',
    acceptedFileTypes: ''
  });

  // Obtener el tema actual
  const theme = useSelector((state) => state.theme.theme) || "light";

  // Función para parsear fecha en formato "18/08/2025 11:03 AM"
  const parseCustomDate = useCallback((dateString) => {
    if (!dateString) return new Date();

    // Si ya es un formato ISO válido, usarlo directamente
    if (dateString.includes('T') || dateString.includes('-')) {
      return new Date(dateString);
    }

    // Parsear formato "DD/MM/YYYY HH:MM AM/PM"
    const parts = dateString.split(' ');
    if (parts.length >= 2) {
      const datePart = parts[0]; // "18/08/2025"
      const timePart = parts[1]; // "11:03"
      const ampm = parts[2]; // "AM" o "PM"

      const [day, month, year] = datePart.split('/');
      const [hours, minutes] = timePart.split(':');

      let hour24 = parseInt(hours);
      if (ampm === 'PM' && hour24 !== 12) hour24 += 12;
      if (ampm === 'AM' && hour24 === 12) hour24 = 0;

      return new Date(year, month - 1, day, hour24, minutes);
    }

    return new Date(dateString);
  }, []);

  // Función para aplicar filtros (optimizada con useCallback)
  const applyFilters = useCallback((files) => {
    return files.filter(file => {
      const matchesTipo = !filters.tipo || file.tipo_archivo === filters.tipo;
      const matchesUsuario = !filters.usuario ||
        (file.usuario?.full_name && file.usuario.full_name.toLowerCase().includes(filters.usuario.toLowerCase()));

      let matchesFecha = true;
      if (filters.fechaDesde || filters.fechaHasta) {
        const fileDate = parseCustomDate(file.fecha_creacion);
        const fechaDesde = filters.fechaDesde ? new Date(filters.fechaDesde) : null;
        const fechaHasta = filters.fechaHasta ? new Date(filters.fechaHasta + 'T23:59:59') : null;

        if (fechaDesde && fileDate < fechaDesde) matchesFecha = false;
        if (fechaHasta && fileDate > fechaHasta) matchesFecha = false;
      }

      return matchesTipo && matchesUsuario && matchesFecha;
    });
  }, [filters, parseCustomDate]);

  // Función para manejar cambios en filtros (optimizada con useCallback)
  const handleFilterChange = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  // Función para limpiar filtros (optimizada con useCallback)
  const clearFilters = useCallback(() => {
    setFilters({
      tipo: '',
      usuario: '',
      fechaDesde: '',
      fechaHasta: ''
    });
  }, []);

  // Obtener usuarios únicos para el filtro (optimizado con useMemo)
  const uniqueUsers = useMemo(() => {
    const users = filesData.map(file => file.usuario?.full_name).filter(Boolean);
    return [...new Set(users)];
  }, [filesData]);

  // Obtener tipos únicos para el filtro (optimizado con useMemo)
  const uniqueTypes = useMemo(() => {
    const types = filesData.map(file => file.tipo_archivo).filter(Boolean);
    return [...new Set(types)];
  }, [filesData]);

  // Cargar y (re)setear la lista de archivos (primera página)
  const loadUploadList = async (url = null) => {
    try {
      const response = await getUploadList(url);
      if (response && response.data) {
        resetData(response.data);
      }
    } catch (error) {
      console.error('Error al cargar la lista de archivos:', error);
    }
  };

  // Ya no usamos cambio de página manual; el scroll infinito carga automáticamente

  // Cargar datos al montar el componente y configurar actualización automática
  useEffect(() => {
    loadUploadList();
  }, []);

  // Efecto separado para la actualización automática continua
  useEffect(() => {
    const interval = setInterval(async () => {
      // Actualizar progreso de los elementos ya cargados sin resetear la lista
      try {
        const response = await getUploadList();
        if (response && response.data && response.data.results) {
          updateItems(response.data.results);
        }
      } catch (err) {
        console.error('Error actualizando progreso:', err);
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [updateItems]);

  // Configurar el root del IntersectionObserver al contenedor scrollable
  useEffect(() => {
    if (tableWrapperRef.current) {
      setObserverRoot(tableWrapperRef.current);
    }
  }, [setObserverRoot]);

  // Aplicar filtros a los archivos (optimizado con useMemo)
  const filteredFilesData = useMemo(() => applyFilters(filesData), [filesData, applyFilters]);

  // Actualizar estado cuando cambien los filtros
  useEffect(() => {
    setFilteredFiles(filteredFilesData);
  }, [filteredFilesData]);

  const uploadCatastro = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await postUploadCatastro(formData);
    console.log('Respuesta del servidor:', response);

    return { success: response.status === 202 };
  };

  const uploadCiudadLimpia = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await postUploadCiudadLimpia(formData);
    console.log('Respuesta del servidor Ciudad Limpia:', response);

    return { success: response.status === 202 };
  };

  const uploadRadicadosDiarios = async (file, executionDate = null) => {
    const formData = new FormData();
    formData.append('file', file);
    if (executionDate) {
      formData.append('fecha_ejecucion', executionDate);
    }

    const response = await postUploadRadicadosDiarios(formData);
    console.log('Respuesta del servidor Radicados Diarios:', response);

    return { success: response.status === 202 };
  };

  const uploadEscombros = async (file, executionDate = null) => {
    const formData = new FormData();
    formData.append('file', file);
    if (executionDate) {
      formData.append('fecha_ejecucion', executionDate);
    }

    const response = await postUploadEscombros(formData);
    console.log('Respuesta del servidor Plano Escombros:', response);

    return { success: response.status === 202 };
  };

  const uploadCau = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await postUploadCau(formData);
    console.log('Respuesta del servidor Campos CAU:', response);

    return { success: response.status === 200 };
  };

  const uploadDatosBasicos = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await postUploadDatosBasicos(formData);
    console.log('Respuesta del servidor Datos Básicos:', response);

    return { success: response.status === 202 };
  };

  const uploadProcesosComerciales = async (file, tipoCaso) => {
    const formData = new FormData();
    formData.append('file', file);
    if (tipoCaso) {
      formData.append('tipo_caso', tipoCaso);
    }

    const response = await postUploadProcesosComerciales(formData);
    console.log('Respuesta del servidor Procesos Comerciales:', response);

    return { success: response.status >= 200 && response.status < 300 };
  };

  const uploadCorrespondencia = async (file, executionDate = null) => {
    const formData = new FormData();
    formData.append('file', file);
    if (executionDate) {
      formData.append('fecha_ejecucion', executionDate);
    }

    const response = await postUploadCorrespondencia(formData);
    console.log('Respuesta del servidor Correspondencia:', response);

    return { success: response.status === 202 };
  };

  // Función para abrir el modal con configuración específica
  const openDateModal = (uploadType) => {
    const modalConfigs = {
      radicadosDiarios: {
        title: 'Subir Radicados Diarios',
        description: 'Selecciona la fecha de ejecución y el archivo de radicados diarios',
        acceptedFileTypes: '.txt'
      },
      planoEscombros: {
        title: 'Subir Plano Escombros',
        description: 'Selecciona la fecha de ejecución y el archivo de plano escombros',
        acceptedFileTypes: '.txt'
      },
      correspondencia: {
        title: 'Subir Correspondencia',
        description: 'Selecciona la fecha de ejecución y el archivo de correspondencia',
        acceptedFileTypes: '.txt'
      }
    };

    const config = modalConfigs[uploadType];
    if (config) {
      setModalState({
        isOpen: true,
        uploadType,
        ...config
      });
    }
  };

  // Abrir modal de Procesos Comerciales (sin cargar opciones dinámicas)
  const openCommercialModal = () => {
    setCommercialModal((prev) => ({ ...prev, isOpen: true }));
  };

  const closeCommercialModal = () => {
    setCommercialModal((prev) => ({ ...prev, isOpen: false }));
  };

  const handleCommercialUpload = async (file, tipoCaso) => {
    if (!file) {
      alert('Por favor, selecciona un archivo para Procesos Comerciales');
      return;
    }
    if (!tipoCaso) {
      alert('Por favor, selecciona una Fuente (tipo de caso)');
      return;
    }
    if (!file.name.endsWith('.txt')) {
      alert('Por favor, sube un archivo .txt para Procesos Comerciales');
      return;
    }

    const newFile = {
      id: Date.now(),
      type: 'procesosComerciales',
      name: file.name,
      size: (file.size / 1024).toFixed(2) + ' KB',
      date: new Date().toLocaleString(),
      status: 'loading'
    };

    setRecentFiles(prev => [newFile, ...prev.slice(0, 4)]);
    setUploadStatus(prev => ({ ...prev, procesosComerciales: 'loading' }));
    setIsUploading(true);

    try {
      const result = await uploadProcesosComerciales(file, tipoCaso);
      const status = result.success ? 'success' : 'error';

      setUploadStatus(prev => ({ ...prev, procesosComerciales: status }));
      setRecentFiles(prev => prev.map(f => f.id === newFile.id ? { ...f, status } : f));

      if (result.success) {
        setTimeout(() => loadUploadList(), 1000);
      }
    } catch (error) {
      console.error('Error al subir Procesos Comerciales:', error);
      setUploadStatus(prev => ({ ...prev, procesosComerciales: 'error' }));
      setRecentFiles(prev => prev.map(f => f.id === newFile.id ? { ...f, status: 'error' } : f));
    } finally {
      setIsUploading(false);
      closeCommercialModal();
    }

    setTimeout(() => {
      setUploadStatus(prev => ({ ...prev, procesosComerciales: null }));
    }, 3000);
  };

  // Función para cerrar el modal
  const closeModal = () => {
    setModalState({
      isOpen: false,
      uploadType: null,
      title: '',
      description: '',
      acceptedFileTypes: ''
    });
  };

  // Función para manejar la subida con fecha desde el modal
  const handleDateUpload = async (file, executionDate) => {
    const { uploadType } = modalState;

    if (!file || !executionDate) return;

    const newFile = {
      id: Date.now(),
      type: uploadType,
      name: file.name,
      size: (file.size / 1024).toFixed(2) + ' KB',
      date: new Date().toLocaleString(),
      status: 'loading'
    };

    setRecentFiles(prev => [newFile, ...prev.slice(0, 4)]);
    setUploadStatus(prev => ({ ...prev, [uploadType]: 'loading' }));
    setIsUploading(true);

    try {
      let result;

      if (uploadType === 'radicadosDiarios') {
        result = await uploadRadicadosDiarios(file, executionDate);
      } else if (uploadType === 'planoEscombros') {
        result = await uploadEscombros(file, executionDate);
      } else if (uploadType === 'correspondencia') {
        result = await uploadCorrespondencia(file, executionDate);
      }

      const status = result.success ? 'success' : 'error';

      setUploadStatus(prev => ({ ...prev, [uploadType]: status }));
      setRecentFiles(prev =>
        prev.map(f =>
          f.id === newFile.id ? { ...f, status } : f
        )
      );

      // Recargar la lista después de subir
      if (result.success) {
        setTimeout(() => loadUploadList(), 1000);
      }
    } catch (error) {
      console.error('Error al subir archivo:', error);
      setUploadStatus(prev => ({ ...prev, [uploadType]: 'error' }));
      setRecentFiles(prev =>
        prev.map(f =>
          f.id === newFile.id ? { ...f, status: 'error' } : f
        )
      );
    } finally {
      setIsUploading(false);
    }

    setTimeout(() => {
      setUploadStatus(prev => ({ ...prev, [uploadType]: null }));
    }, 3000);
  };

  const handleFileChange = async (key, e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (key === 'catastroEnel' && !file.name.endsWith('.txt')) {
      alert('Por favor, sube un archivo .txt para Catastro Enel');
      return;
    }

    if (key === 'camposCau' && !file.name.match(/\.(xlsx|xls)$/i)) {
      alert('Por favor, sube un archivo Excel (.xlsx o .xls) para Campos CAU');
      return;
    }

    if (key === 'datosBasicos' && !file.name.endsWith('.txt')) {
      alert('Por favor, sube un archivo .txt para Datos Básicos');
      return;
    }

    const newFile = {
      id: Date.now(),
      type: key,
      name: file.name,
      size: (file.size / 1024).toFixed(2) + ' KB',
      date: new Date().toLocaleString(),
      status: 'loading'
    };

    setRecentFiles(prev => [newFile, ...prev.slice(0, 4)]);
    setUploadStatus(prev => ({ ...prev, [key]: 'loading' }));
    setIsUploading(true);

    if (key === 'catastroEnel') {
      try {
        const result = await uploadCatastro(file);
        const status = result.success ? 'success' : 'error';

        setUploadStatus(prev => ({ ...prev, [key]: status }));
        setRecentFiles(prev =>
          prev.map(f =>
            f.id === newFile.id ? { ...f, status } : f
          )
        );

        // Recargar la lista después de subir
        if (result.success) {
          setTimeout(() => loadUploadList(), 1000);
        }
      } catch (error) {
        setUploadStatus(prev => ({ ...prev, [key]: 'error' }));
        setRecentFiles(prev =>
          prev.map(f =>
            f.id === newFile.id ? { ...f, status: 'error' } : f
          )
        );
      } finally {
        setIsUploading(false);
      }
    } else if (key === 'catastroEstructura') {
      try {
        const result = await uploadCiudadLimpia(file);
        const status = result.success ? 'success' : 'error';

        setUploadStatus(prev => ({ ...prev, [key]: status }));
        setRecentFiles(prev =>
          prev.map(f =>
            f.id === newFile.id ? { ...f, status } : f
          )
        );

        // Recargar la lista después de subir
        if (result.success) {
          setTimeout(() => loadUploadList(), 1000);
        }
      } catch (error) {
        setUploadStatus(prev => ({ ...prev, [key]: 'error' }));
        setRecentFiles(prev =>
          prev.map(f =>
            f.id === newFile.id ? { ...f, status: 'error' } : f
          )
        );
      } finally {
        setIsUploading(false);
      }
    } else if (key === 'radicadosDiarios') {
      if (!file.name.endsWith('.txt')) {
        alert('Por favor, sube un archivo .txt para Radicados Diarios');
        return;
      }
      try {
        const result = await uploadRadicadosDiarios(file);
        const status = result.success ? 'success' : 'error';

        setUploadStatus(prev => ({ ...prev, [key]: status }));
        setRecentFiles(prev =>
          prev.map(f =>
            f.id === newFile.id ? { ...f, status } : f
          )
        );

        // Recargar la lista después de subir
        if (result.success) {
          setTimeout(() => loadUploadList(), 1000);
        }
      } catch (error) {
        setUploadStatus(prev => ({ ...prev, [key]: 'error' }));
        setRecentFiles(prev =>
          prev.map(f =>
            f.id === newFile.id ? { ...f, status: 'error' } : f
          )
        );
      } finally {
        setIsUploading(false);
      }
    } else if (key === 'planoEscombros') {
      if (!file.name.endsWith('.txt')) {
        alert('Por favor, sube un archivo .txt para Plano Escombros');
        return;
      }
      try {
        const result = await uploadEscombros(file);
        const status = result.success ? 'success' : 'error';

        setUploadStatus(prev => ({ ...prev, [key]: status }));
        setRecentFiles(prev =>
          prev.map(f =>
            f.id === newFile.id ? { ...f, status } : f
          )
        );

        // Recargar la lista después de subir
        if (result.success) {
          setTimeout(() => loadUploadList(), 1000);
        }
      } catch (error) {
        setUploadStatus(prev => ({ ...prev, [key]: 'error' }));
        setRecentFiles(prev =>
          prev.map(f =>
            f.id === newFile.id ? { ...f, status: 'error' } : f
          )
        );
      } finally {
        setIsUploading(false);
      }
    } else if (key === 'camposCau') {
      if (!file.name.match(/\.(xlsx|xls)$/i)) {
        alert('Por favor, sube un archivo Excel (.xlsx o .xls) para Campos CAU');
        return;
      }
      try {
        const result = await uploadCau(file);
        const status = result.success ? 'success' : 'error';

        setUploadStatus(prev => ({ ...prev, [key]: status }));
        setRecentFiles(prev =>
          prev.map(f =>
            f.id === newFile.id ? { ...f, status } : f
          )
        );

        // Recargar la lista después de subir
        if (result.success) {
          setTimeout(() => loadUploadList(), 1000);
        }
      } catch (error) {
        setUploadStatus(prev => ({ ...prev, [key]: 'error' }));
        setRecentFiles(prev =>
          prev.map(f =>
            f.id === newFile.id ? { ...f, status: 'error' } : f
          )
        );
      } finally {
        setIsUploading(false);
      }
    } else if (key === 'datosBasicos') {
      if (!file.name.endsWith('.txt')) {
        alert('Por favor, sube un archivo .txt para Datos Básicos');
        return;
      }
      try {
        const result = await uploadDatosBasicos(file);
        const status = result.success ? 'success' : 'error';

        setUploadStatus(prev => ({ ...prev, [key]: status }));
        setRecentFiles(prev =>
          prev.map(f =>
            f.id === newFile.id ? { ...f, status } : f
          )
        );

        // Recargar la lista después de subir
        if (result.success) {
          setTimeout(() => loadUploadList(), 1000);
        }
      } catch (error) {
        setUploadStatus(prev => ({ ...prev, [key]: 'error' }));
        setRecentFiles(prev =>
          prev.map(f =>
            f.id === newFile.id ? { ...f, status: 'error' } : f
          )
        );
      } finally {
        setIsUploading(false);
      }
    } else {
      setTimeout(() => {
        const isSuccess = Math.random() > 0.3;
        const status = isSuccess ? 'success' : 'error';

        setUploadStatus(prev => ({ ...prev, [key]: status }));
        setRecentFiles(prev =>
          prev.map(f =>
            f.id === newFile.id ? { ...f, status } : f
          )
        );
        setIsUploading(false);
      }, 1500);
    }

    setTimeout(() => {
      setUploadStatus(prev => ({ ...prev, [key]: null }));
    }, 3000);
  };

  const getIconForType = (type) => {
    switch (type) {
      case 'catastroEnel':
      case 'CATASTRO_ENEL':
        return <FiZap className="card-icon" />;
      case 'planoDatosBasicos': return <FiHome className="card-icon" />;
      case 'datosBasicos':
      case 'DATOS_BASICOS':
        return <FiHome className="card-icon" />;
      case 'planoEscombros':
      case 'PLANO_ESCOMBROS':
        return <FiTruck className="card-icon" />;
      case 'catastroEstructura':
      case 'CATASTRO_ESTRUCTURA':
        return <FiLayers className="card-icon" />;
      case 'radicadosDiarios':
      case 'RADICADOS_DIARIOS':
        return <FiFileText className="card-icon" />;
      case 'correspondencia':
      case 'CORRESPONDENCIA':
        return <FiMail className="card-icon" />;
      case 'camposCau':
      case 'CAMPOS_CAU':
        return <FiDatabase className="card-icon" />;
      case 'procesosComerciales':
      case 'PROCESOS_COMERCIALES':
        return <FiBriefcase className="card-icon" />;
      default: return <FiUpload className="card-icon" />;
    }
  };

  const getStatusDisplay = (file) => {
    if (file.estado === 'PROCESANDO') {
      return (
        <div className="status-badge processing">
          <span className="loading-spinner"><FiLoader className="spinner" /></span>
          Procesando
        </div>
      );
    } else if (file.estado === 'COMPLETADO') {
      return <span className="status-badge success">Completado</span>;
    } else if (file.estado === 'ERROR') {
      return <span className="status-badge error">Error</span>;
    } else {
      return <span className={`status-badge ${file.estado?.toLowerCase?.()}`}>{file.estado}</span>;
    }
  };

  const uploadOptions = [
    { key: 'catastroEnel', title: 'Catastro Enel', description: 'Archivos de catastro eléctrico (formato .txt)' },
    { key: 'planoEscombros', title: 'Plano Escombros', description: 'Planos de gestión de escombros' },
    { key: 'catastroEstructura', title: 'Catastro Estructura al Día', description: 'Aseo (Ciudad Limpia)' },
    { key: 'radicadosDiarios', title: 'Radicados Diarios', description: 'Gestión de PQRS diarios' },
    { key: 'correspondencia', title: 'Correspondencia', description: 'Carga de correspondencia (formato .txt)' },
    { key: 'camposCau', title: 'Campos CAU', description: 'Actualiza las zonas de las cuentas (formato Excel)' },
    { key: 'datosBasicos', title: 'Datos Básicos', description: 'Información básica de facturación (formato .txt)' },
    { key: 'procesosComerciales', title: 'Procesos Comerciales', description: 'Carga de procesos comerciales (formato .txt)' },
  ];

  return (
    <div className={`dashboard-containerUp page-content ${theme === 'dark' ? 'dark-theme' : ''}`}>
      {/* Header compacto */}
      <div className="dashboard-headerUp">
        <h1 className="dashboard-titleUp" style={{ color: theme === "dark" ? "#c3cbe4" : "" }}>
          Sistema de Carga de Documentos
        </h1>
      </div>

      {/* Layout principal horizontal */}
      <div className="main-layout">
        {/* Panel izquierdo - Botones de carga */}
        <div className={`upload-panel ${theme === 'dark' ? 'dark-panel' : ''}`}>
          <div className="upload-header">
            <h3 style={{ color: theme === "dark" ? "#c3cbe4" : "" }}>
              <FiUpload className="header-icon" />
              Cargar Archivos
            </h3>
          </div>

          <div className="upload-buttons-container">
            {uploadOptions.map((option, index) => (
              <React.Fragment key={option.key}>
                <input
                  id={option.key}
                  type="file"
                  className="file-input"
                  onChange={(e) => handleFileChange(option.key, e)}
                  accept={
                    option.key === 'catastroEnel' || option.key === 'radicadosDiarios' || option.key === 'planoEscombros' || option.key === 'datosBasicos' || option.key === 'correspondencia'
                      ? '.txt'
                      : option.key === 'camposCau'
                        ? '.xlsx,.xls'
                        : '*'
                  }
                  disabled={isUploading}
                />
                <div
                  className={`upload-button ${uploadStatus[option.key] ? uploadStatus[option.key] : ''} ${isUploading && uploadStatus[option.key] === 'loading' ? 'uploading' : ''} ${theme === 'dark' ? 'dark-button' : ''} ${option.key === 'planoEscombros' ? 'escombros-button' : ''} ${option.key === 'radicadosDiarios' ? 'radicados-button' : ''} ${option.key === 'correspondencia' ? 'correspondencia-button' : ''} ${option.key === 'procesosComerciales' ? 'comerciales-button' : ''}`}
                  style={{ animationDelay: `${index * 0.05}s` }}
                  onClick={() => {
                    if (isUploading) return;

                    // Para radicados, escombros y correspondencia, abrir modal con fecha
                    if (option.key === 'radicadosDiarios' || option.key === 'planoEscombros' || option.key === 'correspondencia') {
                      openDateModal(option.key);
                    } else if (option.key === 'procesosComerciales') {
                      openCommercialModal();
                    } else {
                      // Para otros archivos, usar el método tradicional
                      document.getElementById(option.key).click();
                    }
                  }}
                >
                  <div className="button-content">
                    <div className="button-icon">
                      {uploadStatus[option.key] === 'loading' ? (
                        <FiLoader className="spinner" />
                      ) : uploadStatus[option.key] === 'success' ? (
                        <FiCheck className="success-icon" />
                      ) : uploadStatus[option.key] === 'error' ? (
                        <FiX className="error-icon" />
                      ) : (
                        getIconForType(option.key)
                      )}
                    </div>
                    <div className="button-text">
                      <span className="button-title" style={{ color: theme === "dark" ? "#c3cbe4" : "" }}>
                        {option.title}
                      </span>
                      <span className="button-description" style={{ color: theme === "dark" ? "#8a92b2" : "" }}>
                        {option.description}
                      </span>
                    </div>
                  </div>

                  {uploadStatus[option.key] && (
                    <div className={`status-indicator-mini ${uploadStatus[option.key]}`}>
                      <div className="status-dot"></div>
                    </div>
                  )}
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Panel derecho - Tabla de archivos */}
        <div className={`files-panel ${theme === 'dark' ? 'dark-panel' : ''}`}>
          <div className="files-header">
            <h3 style={{ color: theme === "dark" ? "#c3cbe4" : "" }}>
              <FiFileText className="header-icon" />
              Archivos Recientes
            </h3>

            {/* Filtros debajo del título */}
            <div className="files-header-actions">
              <button
                className={`filter-toggle-btn ${theme === 'dark' ? 'dark-btn' : ''}`}
                onClick={() => setShowFilters(!showFilters)}
                title="Mostrar/Ocultar filtros"
              >
                <FiFilter />
              </button>
              <div className="files-count" style={{ color: theme === "dark" ? "#8a92b2" : "" }}>
                Mostrando {filteredFiles.length} de {filesData.length} cargados{count ? `; Total ${count}` : ''}
              </div>
            </div>

          </div>
          {showFilters && (
            <div className={`filters-container ${theme === 'dark' ? 'dark-filters' : ''}`}>
              <div className="filters-row">
                <div className="filter-group">
                  <label style={{ color: theme === "dark" ? "#c3cbe4" : "" }}>Tipo:</label>
                  <select
                    value={filters.tipo}
                    onChange={(e) => handleFilterChange('tipo', e.target.value)}
                    className={theme === 'dark' ? 'dark-select' : ''}
                  >
                    <option value="">Todos</option>
                    {uniqueTypes.map(type => (
                      <option key={type} value={type}>
                        {type.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase())}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="filter-group">
                  <label style={{ color: theme === "dark" ? "#c3cbe4" : "" }}>Usuario:</label>
                  <input
                    type="text"
                    value={filters.usuario}
                    onChange={(e) => handleFilterChange('usuario', e.target.value)}
                    placeholder="Buscar usuario..."
                    className={theme === 'dark' ? 'dark-input' : ''}
                  />
                </div>

                <div className="filter-group">
                  <label style={{ color: theme === "dark" ? "#c3cbe4" : "" }}>Desde:</label>
                  <input
                    type="date"
                    value={filters.fechaDesde}
                    onChange={(e) => handleFilterChange('fechaDesde', e.target.value)}
                    className={theme === 'dark' ? 'dark-input' : ''}
                  />
                </div>

                <div className="filter-group">
                  <label style={{ color: theme === "dark" ? "#c3cbe4" : "" }}>Hasta:</label>
                  <input
                    type="date"
                    value={filters.fechaHasta}
                    onChange={(e) => handleFilterChange('fechaHasta', e.target.value)}
                    className={theme === 'dark' ? 'dark-input' : ''}
                  />
                </div>

                <div className="filter-actions">
                  <button
                    className={`clear-filters-btn ${theme === 'dark' ? 'dark-btn' : ''}`}
                    onClick={clearFilters}
                    title="Limpiar filtros"
                  >
                    <FiRefreshCw />
                  </button>
                </div>
              </div>
            </div>
          )}
          <div className="files-table-container">
            {filteredFiles.length > 0 ? (
              <>
                <div
                  className="table-wrapper"
                  ref={(el) => {
                    tableWrapperRef.current = el;
                    if (el) setObserverRoot(el);
                  }}
                  onScroll={(e) => {
                    const t = e.currentTarget;
                    // Marcar que el usuario ya interactuó con el scroll
                    setUserScrolled(true);
                    // Si está muy cerca del fondo, asegurar disparo
                    const nearBottom = (t.scrollTop + t.clientHeight) >= (t.scrollHeight - 2);
                    if (nearBottom && hasMore && !loadingMore) {
                      loadMore();
                    }
                  }}
                >
                  <Table responsive hover className={`compact-table ${theme === 'dark' ? 'dark-table' : ''}`}>
                    <thead>
                      <tr>
                        <th style={{ color: theme === "dark" ? "#c3cbe4" : "" }}>Tipo</th>
                        <th style={{ color: theme === "dark" ? "#c3cbe4" : "" }}>Archivo</th>
                        <th style={{ color: theme === "dark" ? "#c3cbe4" : "" }}>Usuario</th>
                        <th style={{ color: theme === "dark" ? "#c3cbe4" : "" }}>Fecha</th>
                        <th style={{ color: theme === "dark" ? "#c3cbe4" : "" }}>Estado</th>
                        <th style={{ color: theme === "dark" ? "#c3cbe4" : "" }}>Progreso</th>
                        <th style={{ color: theme === "dark" ? "#c3cbe4" : "" }}>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredFiles.map((file, index) => (
                        <FileTableRow
                          key={file.id}
                          file={file}
                          theme={theme}
                          index={index}
                        />
                      ))}
                    </tbody>
                  </Table>
                  {/* Scroll infinito: sentinel y estado de carga */}
                  <div className="infinite-scroll-status">
                    {loadingMore && (
                      <div className={`loading-more ${theme === 'dark' ? 'dark-text' : ''}`}>
                        <FiLoader className="spin" /> Cargando más...
                      </div>
                    )}
                    {!hasMore && filesData.length > 0 && (
                      <div className={`end-of-list ${theme === 'dark' ? 'dark-text' : ''}`}>
                        No hay más registros
                      </div>
                    )}
                    <div ref={lastElementRef} style={{ height: '32px' }} />
                  </div>
                </div>
              </>
            ) : (
              <div className="empty-state">
                <div className="empty-icon-container">
                  <FiUpload className="empty-icon" style={{ color: theme === "dark" ? "#8a92b2" : "" }} />
                </div>
                <p className="empty-text" style={{ color: theme === "dark" ? "#8a92b2" : "" }}>
                  No hay archivos cargados aún
                </p>
                <p className="empty-subtext" style={{ color: theme === "dark" ? "#6b7280" : "" }}>
                  Selecciona un tipo de archivo para comenzar
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal para fecha de ejecución */}
      <DateUploadModal
        isOpen={modalState.isOpen}
        onClose={closeModal}
        onUpload={handleDateUpload}
        title={modalState.title}
        description={modalState.description}
        acceptedTypes={modalState.acceptedFileTypes || modalState.acceptedTypes}
      />

      {/* Modal para Procesos Comerciales (Fuente + archivo) */}
      <CommercialUploadModal
        isOpen={commercialModal.isOpen}
        onClose={closeCommercialModal}
        onUpload={handleCommercialUpload}
        title={commercialModal.title}
        description={commercialModal.description}
        acceptedTypes={commercialModal.acceptedTypes}
        isUploading={isUploading}
      />
    </div>
  );
};

export default UploadDashboard;

// Componente de modal para Procesos Comerciales (Fuente + archivo)
const CommercialUploadModal = ({
  isOpen,
  onClose,
  onUpload,
  title,
  description,
  acceptedTypes,
  isUploading = false,
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedTipoCaso, setSelectedTipoCaso] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = React.useRef(null);

  const theme = useSelector((state) => state.theme.theme);
  const isDark = theme === 'dark';

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) setSelectedFile(file);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setSelectedFile(e.dataTransfer.files[0]);
    }
  };

  const handleSubmit = () => {
    if (selectedFile && selectedTipoCaso) {
      onUpload(selectedFile, selectedTipoCaso);
      handleClose();
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setSelectedTipoCaso('');
    setDragActive(false);
    onClose();
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const isValidFile = (file) => {
    if (!acceptedTypes) return true;
    const fileExtension = file.name.split('.').pop().toLowerCase();
    return acceptedTypes.includes(`.${fileExtension}`);
  };

  return (
    <div className={`date-upload-modal modal ${isOpen ? 'show' : ''}`} style={{ display: isOpen ? 'block' : 'none' }}>
      <div className="modal-dialog modal-lg">
        <div className={`modal-content ${isDark ? 'dark-body' : ''}`}>
          <div className={`modal-body-custom ${isDark ? 'dark-body' : ''}`}>
            <h5 className={isDark ? 'dark-label' : ''}>{title}</h5>
            <p className={isDark ? 'dark-subtext' : ''}>{description}</p>

            <div className="date-form-group">
              <label className={`form-label ${isDark ? 'dark-label' : ''}`}>Fuente *</label>
              <select
                className={`date-input ${isDark ? 'dark-input' : ''}`}
                value={selectedTipoCaso}
                onChange={(e) => setSelectedTipoCaso(e.target.value)}
                required
              >
                <option value="">Seleccione una fuente</option>
                <option value="CENSO">CENSO</option>
                <option value="PLAN DE ACTUALIZACION">PLAN DE ACTUALIZACION</option>
                <option value="CARTERA">CARTERA</option>
                <option value="MADRE COMUNITARIA">MADRE COMUNITARIA</option>
              </select>
            </div>

            <div className="file-upload-section">
              <label className={`form-label ${isDark ? 'dark-label' : ''}`}>Seleccionar Archivo *</label>
              <div
                className={`file-drop-zone ${isDark ? 'dark-drop-zone' : ''} ${dragActive ? 'drag-active' : ''}`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <i className="fas fa-cloud-upload-alt drop-zone-icon"></i>
                <p className={`drop-zone-text ${isDark ? 'dark-text' : ''}`}>Haz clic para seleccionar o arrastra el archivo aquí</p>
                <p className={`drop-zone-subtext ${isDark ? 'dark-subtext' : ''}`}>Tipos permitidos: {acceptedTypes}</p>

                <input
                  ref={fileInputRef}
                  type="file"
                  className="file-input-hidden"
                  accept={acceptedTypes}
                  onChange={handleFileSelect}
                />
              </div>

              {selectedFile && (
                <div className={`selected-file-info ${isDark ? 'dark-file-info' : ''}`}>
                  <i className="fas fa-file-alt file-icon"></i>
                  <div className="file-details">
                    <h6 className={isDark ? 'dark-file-name' : ''}>{selectedFile.name}</h6>
                    <p className={`file-size ${isDark ? 'dark-file-size' : ''}`}>{formatFileSize(selectedFile.size)}</p>
                    {!isValidFile(selectedFile) && (
                      <p className="file-error">Tipo de archivo no válido</p>
                    )}
                  </div>
                  <button type="button" className="remove-file-btn" onClick={(e) => { e.stopPropagation(); setSelectedFile(null); }}>
                    <i className="fas fa-times"></i>
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className={`modal-footer-custom ${isDark ? 'dark-footer' : ''}`}>
            <button type="button" className={`btn-cancel ${isDark ? 'dark-cancel' : ''}`} onClick={handleClose}>Cancelar</button>
            <button
              type="button"
              className="btn-upload"
              onClick={handleSubmit}
              disabled={!selectedTipoCaso || !selectedFile || !isValidFile(selectedFile) || isUploading}
            >
              {isUploading ? (
                <>
                  <i className="fas fa-spinner upload-spinner"></i>
                  Subiendo...
                </>
              ) : (
                <>
                  <i className="fas fa-upload"></i>
                  Subir Archivo
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};